import type { SupabaseClient } from "@supabase/supabase-js";

import type { AccessToken, TokenResult } from "./types";
import { classifyAuthError } from "./classifyAuthError";
import { AuthErrorClass } from "./types";

const SKEW_BUFFER_SECONDS = 30;

// Single-flight: at most one in-flight refresh per client instance. Keyed by the
// client object so it is garbage-collected with the client (no leak).
const inFlight = new WeakMap<object, Promise<TokenResult>>();

export const __resetSingleFlightForTests = () => {
  // WeakMap can't be enumerated/cleared; tests pass a fresh client object each time,
  // so there is nothing to reset. Kept so tests document single-flight isolation intent.
};

const nowSeconds = () => Math.floor(Date.now() / 1000);

/**
 * True when the session has no known expiry, or is within the skew window of it.
 * Expiry is sourced from the Supabase session's `expires_at` (Unix seconds) — the
 * same field supabase-js's own auto-refresh uses — so we never decode the JWT
 * ourselves (no `atob`/base64 dependency, no decode-failure edge).
 */
const isExpiredSoon = (expiresAt: number | undefined): boolean => {
  if (typeof expiresAt !== "number") return true; // unknown expiry → refresh
  return expiresAt - nowSeconds() <= SKEW_BUFFER_SECONDS;
};

// session_not_found arrives as AuthSessionMissingError with no code (auth-js lib/fetch.js).
const isDeadSessionError = (error: { name?: string; code?: string }) =>
  error.name === "AuthSessionMissingError" ||
  classifyAuthError(error.code) === AuthErrorClass.GENUINE;

const refresh = async (
  client: SupabaseClient,
  refreshToken: string | undefined,
): Promise<TokenResult> => {
  try {
    const { data, error } = await client.auth.refreshSession();
    if (error) {
      if (isDeadSessionError(error)) {
        // auth-js 2.118 keeps a session whose refresh token is dead until the access token
        // expires and emits no SIGNED_OUT: end it here, only the session that failed (a
        // concurrent caller may have saved a newer one). Reached when this helper refreshes
        // (forced, or 30 s left); inside auth-js's 90 s margin its own failed refresh keeps the
        // still-valid session. A null read means auth-js removed the session and sent
        // SIGNED_OUT, or, in its 60 s failure cooldown, that storage still holds the dead
        // session with no event yet: the "auth" answer lets the GraphQL client end it.
        const stored = (await client.auth.getSession()).data.session;
        const storedToken = stored?.refresh_token;
        // The winner is not this caller's failure: "auth" would make the GraphQL client sign
        // it out too, so the stale request fails as transient and is never replayed under it.
        if (stored && storedToken !== refreshToken) {
          return { ok: false, reason: "unknown" };
        }
        if (storedToken === refreshToken) {
          await client.auth.signOut({ scope: "local" }).catch(() => undefined);
        }
        return { ok: false, reason: "auth" };
      }
      return { ok: false, reason: "network" };
    }
    const token = data.session?.access_token;
    return token
      ? { ok: true, token: token as AccessToken }
      : { ok: false, reason: "auth" };
  } catch {
    // Thrown (AuthRetryableFetchError / network) → transient, never sign out.
    return { ok: false, reason: "network" };
  }
};

/**
 * Returns a non-expired access token, refreshing single-flight if needed. Spec §3.1.
 * @param opts.forceRefresh skip the valid-token short-circuit and refresh (used by the
 *   GraphQL retry-once path after a server-side token rejection).
 */
export const getValidAccessToken = async (
  client: SupabaseClient,
  opts?: { forceRefresh?: boolean },
): Promise<TokenResult> => {
  try {
    const { data, error } = await client.auth.getSession();
    // getSession refreshes expired tokens too. A failed read is not a missing session.
    if (error) {
      return {
        ok: false,
        reason: isDeadSessionError(error) ? "auth" : "network",
      };
    }
    const session = data.session;
    const current = session?.access_token ?? null;
    if (!current) return { ok: false, reason: "auth" };
    if (!opts?.forceRefresh && !isExpiredSoon(session?.expires_at)) {
      return { ok: true, token: current as AccessToken };
    }
    // Near/past expiry (or forced) → single-flight refresh.
    const existing = inFlight.get(client);
    if (existing) return existing;
    const promise = refresh(client, session?.refresh_token).finally(() =>
      inFlight.delete(client),
    );
    inFlight.set(client, promise);
    return promise;
  } catch {
    return { ok: false, reason: "network" };
  }
};
